import React, { useMemo, useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import isEmpty from "lodash/isEmpty";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { Formik, Form, useFormikContext } from "formik";
import Header from "../../../../../../components/wizard/Header";
import Footer from "../../../../../../components/wizard/Footer/Footer";
import useMutation from "../../../../../../hooks/useMutation";
import { UPDATE_INTERNAL_WORK_ORDER_MUTATION } from "../../../../../../constants/graphql/mutations";
import ChargeSection from "../../../../../../components/capture/ReceivingWizard/AdditionalCharges/ChargeSection";
import EmptyAdditionalCharges from "../../../../../../components/capture/ReceivingWizard/AdditionalCharges/EmptyAdditionalCharges";
import RateCategoryPickerModal from "../../../../../../components/capture/ReceivingWizard/AdditionalCharges/RateCategoryPickerModal";
import RateMultiSelectModal from "../../../../../../components/capture/ReceivingWizard/AdditionalCharges/RateMultiSelectModal";
import {
  getSectionsWithOneTimeCharges,
  buildInitialValues,
  validateCharges,
} from "../../../../../../components/capture/ReceivingWizard/AdditionalCharges/utils";
import { categories } from "../../../../../../components/capture/ReceivingWizard/AdditionalCharges/constants";
import useHasAdditionalCharges from "src/components/capture/ReceivingWizard/hooks/useHasAdditionalCharges";
import useHasEnabledIntegration from "src/hooks/useHasEnabledIntegration";
function AncillaryChargesForm({ internalWorkOrder, project, onNoAdditionalCharges, hideOneTimeCharges, setHideOneTimeCharges }) {
  const navigate = useNavigate();
  const { isSubmitting, values } = useFormikContext();
  const [openCategoryPicker, setOpenCategoryPicker] = useState(false);
  const [openRatePicker, setOpenRatePicker] = useState(null);
  const hasAdditionalCharges = useHasAdditionalCharges(project, "outbound");
  const { hasIntegration } = useHasEnabledIntegration(project);
  const totalSteps = (hasAdditionalCharges ? 4 : 3) + (hasIntegration ? 1 : 0);
  const list = Object.entries(values).map(([key, value]) => ({
    key,
    entries: Object.entries(value),
  }));
  const internalCategories = categories.filter(
    cat => cat.key === "materialRates" || cat.key === "laborRates" || cat.key === "equipmentRates" || cat.key === "oneTimeCharge" || cat.key === "noAdditionalCharges"
  );
  const handleAddBillingCharges = () => {
    if (!internalWorkOrder?.oneTimeCharges?.no_additional_charges) {
      setHideOneTimeCharges(false);
    }
    setOpenCategoryPicker(true);
  };
  const handleSelectCategory = categoryKey => {
    setOpenCategoryPicker(false);
    if (categoryKey === "oneTimeCharge") {
    } else if (categoryKey === "noAdditionalCharges") {
      onNoAdditionalCharges();
    } else {
      setOpenRatePicker({ categoryKey });
    }
  };
  const handleBack = () => {
    const params = new URLSearchParams(window.location.search);
    params.set("step", hasIntegration ? "integration" : "items");
    navigate({ search: params.toString() });
  };
  const hasNoChargesConfirmed = internalWorkOrder?.oneTimeCharges?.no_additional_charges !== undefined;
  const isFormValid = list.length > 0 || hasNoChargesConfirmed;
  return (
    <>
      <Form id="ancillary-charges-form" style={{ height: "100%" }}>
        <Box display="flex" flexDirection="column" justifyContent="space-between" minHeight="100%">
          <Header title="Ancillary Charges" info={internalWorkOrder.internalWorkOrderNumber} />
          <Box px={2} mt={2} flexGrow={1} sx={{ overflowY: "auto", minHeight: 320 }}>
            {internalWorkOrder?.oneTimeCharges?.no_additional_charges !== undefined &&
            !hideOneTimeCharges ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight={320}
                gap={2}
              >
                <Typography variant="h6" color="success.main" align="center">
                  ✓ No Ancillary Charges Confirmed
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  This internal work order has been confirmed to have no ancillary charges.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setHideOneTimeCharges(true);
                    setOpenCategoryPicker(true);
                  }}
                  size="small"
                >
                  Add Charges Instead
                </Button>
              </Box>
            ) : isEmpty(values) ? (
              <EmptyAdditionalCharges
                description="Internal work orders may include extra charges for materials, labor, or equipment."
                onAddBillingCharges={handleAddBillingCharges}
              />
            ) : (
              <>
                {list.map(sec => <ChargeSection key={sec.key} section={sec} timeIncrementMinutes={project?.settings?.time_increment_minutes} />)}
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddBillingCharges}
                  >
                    Add More Charges
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Form>
      <Box mt={2}>
        <Footer
          step={2}
          steps={totalSteps}
          startTime={internalWorkOrder?.pickingStartTime}
          completeTime={internalWorkOrder?.pickingCompleteTime}
        >
          <div>
            <Button
              onClick={handleBack}
              variant="outlined"
              sx={{ mr: 1, textTransform: "none" }}
            >
              Back
            </Button>
            <Button
              type="submit"
              variant="contained"
              form="ancillary-charges-form"
              sx={{ textTransform: "none" }}
              disabled={isSubmitting || !isFormValid}
            >
              Next
            </Button>
          </div>
        </Footer>
      </Box>
      {openCategoryPicker && (
        <RateCategoryPickerModal
          onClose={() => setOpenCategoryPicker(false)}
          onSelect={handleSelectCategory}
          hasExistingCharges={list.length > 0}
          categories={internalCategories}
        />
      )}
      {openRatePicker && (
        <RateMultiSelectModal
          onClose={() => setOpenRatePicker(null)}
          categoryKey={openRatePicker.categoryKey}
          rates={project?.[openRatePicker.categoryKey] || {}}
          initialSelected={Object.keys(
            {
              materialRates: values?.materialCharges,
              laborRates: values?.laborCharges,
              equipmentRates: values?.equipmentCharges,
            }[openRatePicker.categoryKey] || {}
          )}
        />
      )}
    </>
  );
}
export default function AncillaryCharges({ project, internalWorkOrder }) {
  const navigate = useNavigate();
  const [updateInternalWorkOrder] = useMutation(UPDATE_INTERNAL_WORK_ORDER_MUTATION);
  const [hideOneTimeCharges, setHideOneTimeCharges] = useState(false);

  useEffect(() => {
    const warehouses = internalWorkOrder?.warehousesFromInternalItems || [];
    const allWarehousesExcluded = warehouses.length > 0 && warehouses.every(w => w.excludeFromBilling);
    if (allWarehousesExcluded) {
      const params = new URLSearchParams(window.location.search);
      params.set("step", "confirmation");
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [internalWorkOrder, navigate]);
  const sections = useMemo(
    () => {
      const allSections = getSectionsWithOneTimeCharges(project, "outbound_enabled", internalWorkOrder);
      return allSections.filter(section =>
        section.key === "materialCharges" ||
        section.key === "laborCharges" ||
        section.key === "equipmentCharges" ||
        section.key === "oneTimeCharges"
      );
    },
    [project, internalWorkOrder]
  );
  const initialValues = useMemo(
    () => buildInitialValues(sections, internalWorkOrder),
    [sections, internalWorkOrder]
  );
  const hasNoChargesConfirmed = internalWorkOrder?.oneTimeCharges?.no_additional_charges !== undefined;
  const skipValidation = hasNoChargesConfirmed && !hideOneTimeCharges;
  const handleNoAdditionalCharges = useCallback(async () => {
    const fullPayload = {
      billingType: internalWorkOrder.billingType,
      billingQuantities: internalWorkOrder.billingQuantities,
      materialCharges: internalWorkOrder.materialCharges,
      laborCharges: internalWorkOrder.laborCharges,
      equipmentCharges: internalWorkOrder.equipmentCharges,
      oneTimeCharges: { no_additional_charges: 0 },
    };
    try {
      await updateInternalWorkOrder({
        variables: {
          id: internalWorkOrder.id,
          input: fullPayload,
        },
      });
      const params = new URLSearchParams(window.location.search);
      params.set("step", "confirmation");
      navigate({ search: params.toString() });
    } catch (error) {
      console.error("Failed to save no ancillary charges:", error);
    }
  }, [updateInternalWorkOrder, internalWorkOrder, navigate]);
  const handleSubmit = useCallback(
    async (values, { setSubmitting }) => {
      const inputPayload = {};
      Object.entries(values).forEach(([sectionKey, sectionValues]) => {
        const obj = {};
        Object.entries(sectionValues).forEach(([rateKey, value]) => {
          if (value !== "" && value !== undefined && value !== null) {
            obj[rateKey] = Number(value);
          }
        });
        if (Object.keys(obj).length > 0) {
          if (sectionKey === "oneTimeCharges") {
            inputPayload[sectionKey] = {
              list: Object.entries(obj).map(([notes, amount]) => ({ notes, amount })),
            };
          } else {
            inputPayload[sectionKey] = obj;
          }
        }
      });
      const hasActualCharges = Object.keys(inputPayload).some(key =>
        ["materialCharges", "laborCharges", "equipmentCharges", "oneTimeCharges"].includes(key)
      );
      let finalPayload = { ...inputPayload };
      if (hasActualCharges && internalWorkOrder?.oneTimeCharges?.no_additional_charges !== undefined) {
        if (!inputPayload.oneTimeCharges) {
          finalPayload.oneTimeCharges = { list: [] };
        }
      }
      const fullPayload = {
        billingType: internalWorkOrder.billingType,
        billingQuantities: internalWorkOrder.billingQuantities,
        materialCharges: internalWorkOrder.materialCharges,
        laborCharges: internalWorkOrder.laborCharges,
        equipmentCharges: internalWorkOrder.equipmentCharges,
        oneTimeCharges: internalWorkOrder.oneTimeCharges,
        ...finalPayload,
      };
      await updateInternalWorkOrder({
        variables: {
          id: internalWorkOrder.id,
          input: fullPayload,
        },
      });
      setSubmitting(false);
      const params = new URLSearchParams(window.location.search);
      params.set("step", "confirmation");
      navigate({ search: params.toString() });
    },
    [sections, updateInternalWorkOrder, internalWorkOrder, navigate]
  );
  return (
    <Formik
      initialValues={initialValues}
      validate={values => validateCharges(values, skipValidation)}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      <AncillaryChargesForm
        internalWorkOrder={internalWorkOrder}
        project={project}
        onNoAdditionalCharges={handleNoAdditionalCharges}
        hideOneTimeCharges={hideOneTimeCharges}
        setHideOneTimeCharges={setHideOneTimeCharges}
      />
    </Formik>
  );
}

